import { getCurrency, rateDecimals } from '@cambio/core';

/**
 * Display formatting.
 *
 * Numbers arrive from the API as decimal strings and are converted to `number`
 * exactly once - here, at the boundary where they stop being money and start
 * being pixels. Nothing upstream of this file does arithmetic on a float.
 */

const num = (value: string | number): number =>
  typeof value === 'number' ? value : Number.parseFloat(value);

export function formatAmount(
  value: string | number,
  currency?: string,
  { compact = false }: { compact?: boolean } = {},
): string {
  const n = num(value);
  if (!Number.isFinite(n)) return '—';

  const decimals = currency ? (getCurrency(currency)?.decimals ?? 2) : 2;
  return new Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: compact ? 0 : decimals,
    maximumFractionDigits: decimals,
    notation: compact && Math.abs(n) >= 1_000_000 ? 'compact' : 'standard',
  }).format(n);
}

/**
 * Rate precision scales with magnitude: 4.7132 needs four decimals, 0.048916
 * needs six, and showing JPY at two would round every distinct price to the
 * same number.
 */
export function formatRate(
  value: string | number | null | undefined,
  /**
   * Format a *change* in a rate at the precision of the rate it belongs to.
   *
   * A delta of 0.0054 on a 4.67 rate is not a small-magnitude rate needing eight
   * decimals - it is a four-decimal rate that happens to have moved a little.
   * Without this the simulator reports "-0.00543842", which is precision
   * theatre.
   */
  reference?: string | number | null,
): string {
  if (value === null || value === undefined) return '—';
  const n = num(value);
  if (!Number.isFinite(n)) return '—';
  const basis = reference === null || reference === undefined ? n : num(reference);
  const decimals = rateDecimals(Number.isFinite(basis) ? basis : n);
  return new Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

export function formatPercent(
  value: string | number | null | undefined,
  { signed = true, decimals = 2 }: { signed?: boolean; decimals?: number } = {},
): string {
  if (value === null || value === undefined) return '—';
  const n = num(value);
  if (!Number.isFinite(n)) return '—';
  const body = new Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Math.abs(n));
  const sign = !signed ? '' : n > 0 ? '+' : n < 0 ? '−' : '';
  return `${sign}${body}%`;
}

/** Signed money, using a real minus sign rather than a hyphen. */
export function formatSigned(value: string | number, currency?: string): string {
  const n = num(value);
  const body = formatAmount(Math.abs(n), currency);
  return n < 0 ? `−${body}` : n > 0 ? `+${body}` : body;
}

export const currencySymbol = (code: string): string => getCurrency(code)?.symbol ?? code;
export const currencyFlag = (code: string): string => getCurrency(code)?.flag ?? '';

export function currencyName(code: string, locale: string): string {
  const meta = getCurrency(code);
  if (!meta) return code;
  return locale.startsWith('en') ? meta.nameEn : meta.nameZh;
}

export function formatDate(iso: string, locale = 'zh-CN'): string {
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(
    new Date(`${iso}T00:00:00Z`),
  );
}

export function formatMonth(monthKey: string, locale = 'zh-CN'): string {
  const [year, month] = monthKey.split('-');
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long' }).format(
    new Date(Date.UTC(Number(year), Number(month) - 1, 1)),
  );
}

export function formatFullDate(iso: string, locale = 'zh-CN'): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(
    new Date(`${iso}T00:00:00Z`),
  );
}

/** Today in the user's own timezone - not UTC, which can be a day off. */
export function todayLocal(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

/**
 * Class-name joiner.
 *
 * Accepts `unknown` in the falsy slots rather than a narrow union, because the
 * common idiom is `cond && 'class'` where `cond` is whatever the caller has to
 * hand - a number, a ReactNode, an optional string. Anything non-string that
 * survives the filter would be a bug at the call site, so only strings are kept.
 */
export const cx = (...parts: unknown[]): string =>
  parts.filter((part): part is string => typeof part === 'string' && part.length > 0).join(' ');
